import ActivityTab from "component/flex/shipments/tabs/ActivityTab";
import NewDashboardLayout from "component/layout/NewDashboardLayout";
import Loading from "component/loading";
import { ArrowLeft } from "iconsax-reactjs";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FinanceBadge from "component/finance/FinanceBadge";
import { formatDate } from "component/flex/utils";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  cancelFinanceRequest,
  createRepayLoan,
  deleteFinanceDraft,
  fetchFinanceActivities,
  fetchSingleCredit,
  RepayLoan,
} from "api/credit-fetchers";
import useUserDetails from "hooks/useUserDetails";
import MakePayModal from "component/flex/invoice/modal/MakePayModal";
import { GenericBtn } from "./FinanceRequest";
import PinModal from "component/pay/PinModal";
import TransferSuccessModal from "component/pay/TransferSuccessModal";
import { toast } from "react-toastify";
import { Button } from "component/ui/Button";
import { cn } from "lib/utils";
import DeleteFinanceModal from "component/flex/shipments/modals/DeleteFinanceModal";
import {
  displayCurrencyAndAmount,
  ExpectedStatus,
  financeTitles,
  getFinanceDataList,
} from "constants/FinanceConstants";
import ManageFinanceDetails from "./ManageFinanceDetails";
import getInitials, { getUserEmail, getUserFullName } from "utilities/getInitials";
import { ChevronDown } from "lucide-react";
import ActionDropdown from "component/actionDropdown";
import RepayCryptoModal from "../../../component/flex/shipments/modals/RepayCryptoModal";

export const paymentStatusEnums = { REPAID: "REPAID", UNPAID: "UNPAID" };



const ManageFinance = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: financeId } = useParams();
  const { businessId, walletBalance, creditWalletBalance, creditWalletId, walletBallanceId,
    repaymentWalletDetails } =
    useUserDetails();
  const [makeARepayment, setMakeARepayment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [payFull, setPayFull] = useState(false);
  const [paymentTab, setPaymentTab] = useState("Re-payments");
  const [amount, setAmount] = useState("");
  const [showOtp, setShowOtp] = useState("");
  const [reason, setReason] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [showRepayCryptoModal, setShowRepayCryptoModal] = useState(false)
  const [trxReceiptDoc, setTrxReceiptDoc] = useState(null)

  const { data, isLoading: loadingSingleFinance } = useQuery({
    queryKey: [financeId, "getSingleFinance"],
    queryFn: () => fetchSingleCredit({ businessId, financeId }),
  });

  const { data: financeActivity } = useQuery({
    queryKey: [financeId, "getFinanceActivities"],
    queryFn: () => fetchFinanceActivities({ businessId, financeId }),
  });

  const { mutateAsync: handleRepayFinanceFunc } = useMutation({
    mutationFn: (data) => {
      return createRepayLoan({ businessId, data, financeId });
    },
    onSettled: async (data) => {
      if (data?.response?.data?.statusCode === 400) {
        toast.error(data?.response?.data?.message || "Failed to create finance payment");
        return;
      }
      toast.success(data?.message);
      await queryClient.invalidateQueries({ queryKey: [financeId, "getSingleFinance", "getFinanceActivities"] });
      setShowOtp(false);
    },
    onError: async (error) => {
      toast.error("Failed to create finance payment");
    },
  });
  const { mutateAsync: handleRepayCryptoFinance } = useMutation({
    mutationFn: (data) => RepayLoan({ businessId, data, financeId }),
    onSuccess: (response) => {
      if (response?.response?.data?.statusCode !== 400) {
        toast.success(response?.response?.data?.message || "Operation Successful");
        queryClient?.invalidateQueries({
          queryKey: [
          "fetchCreditGroups",
          "getSingleFinance",
          "fetchAllFinancePay",
          "fetchAllFinancePay",
          financeId
        ]
        });
        setShowRepayCryptoModal(false);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });

  const { mutateAsync: handleCancelFinance, isLoading: isCancelingFinanceLoading } = useMutation({
    mutationFn: (id) => cancelFinanceRequest({ businessId, id, reason }),
    onSettled: (response) => {
      if (response?.error === false) {
        toast.success(response?.message);
        queryClient?.invalidateQueries({ queryKey: [financeId, "getSingleFinance"] });
        setReason("");
        setShowDeleteModal(false);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });
  const { mutateAsync: deleteFinance, isLoading: deletingDraftLoading } = useMutation({
    mutationFn: (id) => deleteFinanceDraft({ businessId, id }),
    onSettled: (response) => {
      if (response?.error === false) {
        queryClient.invalidateQueries({
          queryKey: [
          "fetchCreditGroups",
          "getSingleFinance",
          "fetchAllFinancePay",
          "fetchAllFinancePay",
          ]
        });
        toast.success(response?.message);
        navigate(-1);
      } else {
        toast.error(response?.response?.data?.message);
      }
    },
  });

  const financeDetails = data?.data ?? {};
  const isReceivableFinance = data?.data?.financeType === "receivable_finance";
  const isPayableFinance = data?.data?.financeType === "payable_finance";
  const isPaymentFinance = data?.data?.financeType === "payment_finance";
  const {
    financeType,
    currency,
    requestedAmount,
    payableFinance,
    status,
    createdAt,
    tenure,
    bankStatement,
    boardResolution,
    loans,
    totalAmount,
    loanAmount,
    interestAmount,
    repaymentStructure,
    receivableFinance,
    inventoryFinance,
    paymentFinance, interestRate
  } = financeDetails;

  useEffect(() => {
    if (financeDetails?.status === "pending") {
      setPaymentTab('Documents')
    }
  }, [financeDetails?.status]);
  const contractUserInitials = getInitials(
    getUserFullName(isReceivableFinance ? receivableFinance : isPaymentFinance ? paymentFinance : payableFinance)
  );

  const contractUserFullName = getUserFullName(
    isReceivableFinance ? receivableFinance : isPaymentFinance ? paymentFinance : payableFinance
  );

  const contractUserEmail = getUserEmail(receivableFinance ? receivableFinance : isPaymentFinance ? paymentFinance : payableFinance);
  const dataDetails = isReceivableFinance
    ? receivableFinance
    : isPaymentFinance
      ? paymentFinance
      : isPayableFinance
        ? payableFinance
        : inventoryFinance;

  const getPaidAmount = () => {
    const amount = financeDetails?.loans
      ?.filter((item) => item?.paymentStatus === paymentStatusEnums?.REPAID)
      ?.reduce((acc, cur) => (acc += cur?.amount), 0);
    return displayCurrencyAndAmount(currency, amount);
  };
  const amountToPay =
    financeDetails?.loans?.find((item) => item?.paymentStatus === paymentStatusEnums?.UNPAID)
      ?.amount || financeDetails?.totalAmount;

  const totalAmountToPay = financeDetails?.loans
    ?.filter((item) => item?.paymentStatus === paymentStatusEnums?.UNPAID)
    .reduce((acc, cur) => (acc += cur?.amount), 0);

  const dataList = getFinanceDataList({
    loadingSingleFinance,
    status,
    currency,
    requestedAmount,
    financeType,
    createdAt,
    isPaymentFinance,
    totalAmount,
    interestRate,
    interestAmount,
    tenure,
    paymentFinance,
    financeDetails,
    repaymentStructure,
    dataDetails,
    getPaidAmount,
    formatDate
  });
  const disableRepaymentButton = financeDetails?.repayments?.some(l => l.status === 'PENDING') || financeDetails?.repayments?.some(l => l.status === 'APPROVED')

  return (
    <NewDashboardLayout>
      {(loadingSingleFinance || deletingDraftLoading) && (
        <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
          <Loading />
        </div>
      )}
      <main className="py-2 mx-auto">
        <button
          onClick={() => {
            navigate(-1);
          }}
          className="flex items-center bg-transparent border-none outline-none !text-secondary gap-x-2"
        >
          <ArrowLeft className="" /> Back
        </button>
        <section className="mt-2">
          <div className="flex flex-wrap items-center justify-between gap-3 mt-2 md:flex-nowrap">
            <div className="flex items-center gap-x-2 ">
              <div>
                <span className="!text-lg font-light text-gray-70">
                  {financeTitles[financeType]}
                </span>
                <h4 className="text-2xl m-0 font-[450] text-gray-90">
                  {displayCurrencyAndAmount(
                    currency,
                    status === "disbursed" || status === "approved"
                      ? totalAmount
                      : requestedAmount
                  )}
                </h4>
              </div>
              <div className="mt-3">
                <FinanceBadge badgeType={status} />
              </div>
            </div>

            {status === ExpectedStatus.Draft && (
              <div className="flex items-center gap-3">
                <GenericBtn
                  onClick={() => {
                    navigate("/finance/credit/request", {
                      state: financeDetails,
                    });
                  }}
                  text="Continue request"
                  className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-4 text-sm`}
                />

                <Button
                  className="rounded-lg py-[10px] h-[48px] px-4 text-sm"
                  variant="outline"
                  onClick={async () => {
                    setShowCancelModal(true);
                  }}
                >
                  Delete draft
                </Button>
              </div>
            )}
            {status === ExpectedStatus.Pending && (
              <Button
                className="rounded-lg py-[10px] h-[48px] px-4 text-sm"
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                disabled={isCancelingFinanceLoading}
              >
                {isCancelingFinanceLoading ? "Loading..." : "Cancel request"}
              </Button>
            )}
            {status === ExpectedStatus.Disbursed && !isPaymentFinance && (
              <GenericBtn
                onClick={() => {
                  setMakeARepayment(true);
                  const amountToPrefill = financeDetails?.loans?.find(
                    (item) => item?.paymentStatus === "REPAID"
                  )?.amount;
                  setAmount(amountToPrefill);
                }}
                text="Make a repayment"
                className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-4 text-sm`}
              />
            )}

            {status === ExpectedStatus.Disbursed && isPaymentFinance && <>
              <ActionDropdown
                dropClassName={'w-full'}
                element={<Button
                  // disabled={disableRepaymentButton}
                  className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg h-[40px] py-4 px-4 text-sm`}
                >Make a repayment  <ChevronDown size={18} /> </Button>}
                options={[
                  {
                    label: "Pay with your Tradevu wallet",
                    action: () => {
                      setMakeARepayment(true);
                      const amountToPrefill = financeDetails?.loans?.find(
                        (item) => item?.paymentStatus === "REPAID"
                      )?.amount;
                      setAmount(amountToPrefill);
                    },
                    closeAfterClick: true,
                  },
                  {
                    label: "Pay to a wallet address",
                    action: () => setShowRepayCryptoModal(true),
                    closeAfterClick: true,
                  },
                ]} />
            </>}
          </div>
          <section className="flex flex-col gap-6 mt-4 md:flex-row">
            <ManageFinanceDetails
              payableFinance={dataDetails}
              isReceivableFinance={isReceivableFinance}
              contractUserInitials={contractUserInitials}
              contractUserFullName={contractUserFullName}
              contractUserEmail={contractUserEmail}
              currency={currency}
              status={status}
              loanAmount={loanAmount}
              totalAmount={totalAmount}
              dataList={dataList}
              paymentTab={paymentTab}
              setMakeARepayment={setMakeARepayment}
              bankStatement={bankStatement}
              setPaymentTab={setPaymentTab}
              loans={loans}
              boardResolution={boardResolution}
              isPaymentFinance={isPaymentFinance}
              setAmount={setAmount}
              setShowRepayCryptoModal={setShowRepayCryptoModal}
              disableRepaymentButton={disableRepaymentButton}
              financeDetails={financeDetails}
            />
            {/* Activity */}
            <div className="w-full md:w-5/12 mt-2">
              <div>
                <p className="pb-1 text-sm font-normal">Activity log</p>
                <div
                  className={cn(
                    "p-3 py-4 mb-6 rounded-xl",
                    financeActivity?.length ? "" : "bg-white"
                  )}
                >
                  <ActivityTab activity={financeActivity ?? []} />
                </div>
              </div>
            </div>
          </section>
        </section>
      </main>
      {makeARepayment && (
        <MakePayModal
          currency={currency}
          walletBalance={walletBalance}
          onClick={() => {
            if (amount > walletBalance + creditWalletBalance) {
              return toast.error("Amount can not be greater than wallet balance");
            } else {
              setMakeARepayment(false);
              setShowOtp(true);
            }
          }}
          maxAmount={isPaymentFinance ? totalAmount : amountToPay || financeDetails?.requestedAmount}
          isLoading={loadingSingleFinance}
          isRepayment={true}
          show={makeARepayment}
          setShow={(value) => {
            setMakeARepayment(value);
          }}
          paymentType={paymentType}
          setPaymentType={setPaymentType}
          amount={amount}
          setAmount={setAmount}
          payFull={payFull}
          setPayFull={setPayFull}
          payFullAmount={totalAmountToPay}
        />
      )}
      {showOtp && (
        <PinModal
          isInvoice
          isOpen={showOtp}
          goBack={() => {
            setMakeARepayment(true);
            setShowOtp(false);
          }}
          onClose={() => {
            setShowOtp(false);
          }}
          onConfirm={async (data) => {
            const repayFunc = (loans) => {
              if (Array.isArray(loans)) {
                if (!payFull) {
                  return [
                    loans?.find(
                      (item) =>
                        item?.paymentStatus === paymentStatusEnums.UNPAID
                    )?._id,
                  ];
                } else {
                  return loans
                    ?.map(
                      (item) =>
                        item?.paymentStatus === paymentStatusEnums.UNPAID &&
                        item?._id
                    )
                    ?.filter((item) => !!item);
                }
              } else {
                return [loans?._id];
              }
            };
            const walletToUse = {
              "Credit Wallet": [creditWalletId],
              "Naira Wallet": [walletBallanceId],
              both: [creditWalletId, walletBallanceId],
            };
            const payload = {
              pin: data,
              subLoanIds: repayFunc(financeDetails?.loans),
              walletIds: walletToUse[paymentType],
            };
            await handleRepayFinanceFunc(payload);
            await queryClient.invalidateQueries({ queryKey: [financeId, "getSingleFinance", "getFinanceActivities"] })
          }}
        />
      )}
      <TransferSuccessModal />
      <DeleteFinanceModal
        show={showCancelModal || showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setShowCancelModal(false);
        }}
        reason={reason}
        setReason={setReason}
        onSubmit={async () => {
          showCancelModal
            ? await deleteFinance(financeId)
            : await handleCancelFinance(financeId);
          setShowDeleteModal(false);
          setShowCancelModal(false);
        }}
        isLoading={isCancelingFinanceLoading}
        isDeleteFinance={showCancelModal}
      />
      {showRepayCryptoModal && <RepayCryptoModal trxReceiptDoc={trxReceiptDoc}
        amount={totalAmount}
        network={repaymentWalletDetails?.data?.network}
        wallet={repaymentWalletDetails?.data?.accountNumber}
        financeId={financeId}
        setTrxReceiptDoc={setTrxReceiptDoc} show={showRepayCryptoModal} onSubmit={handleRepayCryptoFinance} onClose={() => setShowRepayCryptoModal(false)} />}
    </NewDashboardLayout>
  );
};

export default ManageFinance;
